/**
 * Section registry — maps a route slug to its app-bar label + eyebrow context.
 * Used by the app bar (two-mode header) and, later, the list-screen scaffold.
 * DESIGN_SYSTEM.md §5 (app bar).
 */
export type SectionMeta = {
  /** Page title shown in the app bar / list screen. */
  label: string;
  /** Uppercase indigo eyebrow shown above the title. */
  eyebrow: string;
};

export const SECTION_REGISTRY: Record<string, SectionMeta> = {
  '': { label: 'Overview', eyebrow: 'Dashboard' },
  activity: { label: 'Notifications', eyebrow: 'Activity' },
  users: { label: 'Users', eyebrow: 'User management' },
  'trainer-applications': { label: 'Trainer Applications', eyebrow: 'Review queue' },
  'specialty-certificates': { label: 'Specialty Certificates', eyebrow: 'Review queue' },
  trainers: { label: 'Trainers', eyebrow: 'User management' },
  locations: { label: 'Locations', eyebrow: 'Catalog' },
  sports: { label: 'Sports', eyebrow: 'Catalog' },
  sessions: { label: 'Sessions', eyebrow: 'Operations' },
  bookings: { label: 'Bookings', eyebrow: 'Operations' },
  'session-reviews': { label: 'Session Reviews', eyebrow: 'Moderation' },
  notifications: { label: 'Notifications', eyebrow: 'Messaging' },
  'notification-logs': { label: 'Notification Logs', eyebrow: 'Messaging' },
  'device-tokens': { label: 'Device Tokens', eyebrow: 'Messaging' },
  'session-chats': { label: 'Session Chats', eyebrow: 'Moderation' },
  'direct-chats': { label: 'Direct Chats', eyebrow: 'Moderation' },
  'chat-monitoring': { label: 'Chat Monitoring', eyebrow: 'Moderation' },
  settings: { label: 'Settings', eyebrow: 'System' },
  account: { label: 'Account', eyebrow: 'System' },
  'app-versions': { label: 'App Versions', eyebrow: 'System' },
  'system-health': { label: 'System Health', eyebrow: 'System' },
};

export function getSectionMeta(slug: string): SectionMeta {
  return (
    SECTION_REGISTRY[slug] ?? {
      label: slug
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' '),
      eyebrow: 'Section',
    }
  );
}

export type AppBarMode = 'list' | 'detail';

export type AppBarContext = {
  mode: AppBarMode;
  slug: string;
  meta: SectionMeta;
  /** Record id when in detail mode. */
  recordId?: string;
  /** 'create' | 'edit' | 'show' when relevant. */
  action?: string;
};

/** Derive app-bar context from a pathname. */
export function parseAppBar(pathname: string): AppBarContext {
  if (pathname === '/' || pathname === '') {
    return { mode: 'list', slug: '', meta: getSectionMeta('') };
  }
  const segments = pathname.split('/').filter(Boolean);
  const slug = segments[0];
  const meta = getSectionMeta(slug);
  const second = segments[1];
  const third = segments[2];

  // /slug                       -> list
  // /slug/create                -> detail (create)
  // /slug/:id, /slug/:id/show   -> detail
  // /slug/:id/edit              -> detail (edit)
  if (!second) {
    return { mode: 'list', slug, meta };
  }
  if (second === 'create') {
    return { mode: 'detail', slug, meta, action: 'create' };
  }
  return { mode: 'detail', slug, meta, recordId: second, action: third || 'show' };
}
